import { fetchEventSource } from '@microsoft/fetch-event-source';

import AuthenticationStore from '~/auth/authenticationStore';
import { refreshAccessToken } from '~/auth/refreshAccessToken';
import { LOGIN_CONSTANTS } from '~/features/login/constants';
import { camelizeKeys } from '~/redux/api/transformers/utils';

const authStore = AuthenticationStore();

class RateLimitError extends Error {}
class AuthenticationError extends Error {}

export interface SseConnectionCallbacks {
  /**
   * Receives the parsed (camelized) event data. `eventName` is the SSE named
   * event (the `event:` line), forwarded for servers that key on it (e.g.
   * hermes `thinking`/`token`/`complete`). Consumers that encode their type in
   * the data body can ignore it.
   */
  onMessage: (event: unknown, eventName?: string) => void;
  onOpen?: (res: Response) => void;
  onClose?: () => void;
  onError?: (err: unknown) => void;
  onAuthenticationError?: (err: unknown) => void;
  onRateLimitError?: () => void;
  onTimeout?: () => void;
}

export interface SseConnectionOptions {
  method: 'POST' | 'GET';
  headers?: Record<string, string>;
  /** Serialized request body, forwarded to fetchEventSource (e.g. POST JSON). */
  body?: string;
  openWhenHidden?: boolean;
  timeout?: number;
  /**
   * Top-level payload keys whose value must reach `onMessage` untouched.
   * Camelization recurses, so a nested server document such as a generated
   * workflow specification would otherwise have its own `snake_case` keys
   * rewritten and stop matching what the server expects back.
   */
  keysToExcludeFromCamelization?: string[];
}

/**
 * Creates a standalone SSE connection using fetchEventSource.
 * Unlike the useSse hook, this function has no React dependencies
 * and can be used from RTK Query middleware or other non-React contexts.
 *
 * Returns an AbortController that can be used to close the connection.
 */
export const createSseConnection = (
  url: string,
  callbacks: SseConnectionCallbacks,
  options: SseConnectionOptions = { method: 'GET' },
): AbortController => {
  const abortController = new AbortController();

  const connect = (hasRetried = false) => {
    setupConnection(url, callbacks, options, abortController).catch(
      async (err) => {
        if (abortController.signal.aborted) return;
        if (hasRetried) {
          callbacks.onAuthenticationError?.(err);
        } else if (err instanceof AuthenticationError) {
          try {
            await refreshAccessToken();
          } catch (refreshError) {
            // The stream is over once the refresh itself fails. Nothing else
            // would tell the caller, because the inactivity timer only starts
            // after a connection opens.
            callbacks.onAuthenticationError?.(refreshError);
            return;
          }
          connect(true);
        }
      },
    );
  };

  connect();
  return abortController;
};

const setupConnection = (
  url: string,
  callbacks: SseConnectionCallbacks,
  options: SseConnectionOptions,
  abortController: AbortController,
): Promise<void> => {
  let timeoutId: NodeJS.Timeout | undefined;

  const clearTimeoutTimer = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  };

  const resetTimeoutTimer = () => {
    clearTimeoutTimer();
    if (options.timeout && callbacks.onTimeout) {
      timeoutId = setTimeout(() => {
        callbacks.onTimeout?.();
        abortController.abort();
      }, options.timeout);
    }
  };

  const handleOpen = async (res: Response) => {
    switch (res.status) {
      case 200:
        callbacks.onOpen?.(res);
        resetTimeoutTimer();
        break;
      case 429:
        callbacks.onRateLimitError?.();
        throw new RateLimitError();
      case 401:
        throw new AuthenticationError();
      default:
        throw new Error(`SSE connection failed with status ${res.status}`);
    }
  };

  return fetchEventSource(url, {
    credentials: 'include',
    headers: {
      ...(options.headers || { Accept: 'text/event-stream' }),
      ...(authStore.getCsrf()
        ? { [LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN]: authStore.getCsrf() }
        : {}),
      ...(authStore.getAccessToken()
        ? { Authorization: `Bearer ${authStore.getAccessToken()}` }
        : {}),
    },
    method: options.method,
    body: options.body,
    signal: abortController.signal,
    openWhenHidden: options.openWhenHidden ?? true,
    onclose: () => {
      clearTimeoutTimer();
      callbacks.onClose?.();
    },
    onerror: (err) => {
      clearTimeoutTimer();
      if (
        (err instanceof DOMException && err.name === 'AbortError') ||
        err instanceof AuthenticationError ||
        err instanceof RateLimitError
      ) {
        throw err;
      } else {
        callbacks.onError?.(err);
        throw err;
      }
    },
    onmessage: (event) => {
      if (event.data === '') {
        resetTimeoutTimer();
        return;
      }
      resetTimeoutTimer();
      try {
        const eventData = JSON.parse(event.data);
        const parsedEventData = camelizeKeys(
          eventData,
          options.keysToExcludeFromCamelization,
        );
        callbacks.onMessage(parsedEventData, event.event);
      } catch {
        throw new Error('Failed to parse SSE event data.');
      }
    },
    onopen: handleOpen,
  });
};
